import type { BannerCta } from '../../../../services/ui';

// Hand-synced with admin-frontend (RivePreview) and admin-backend's CTA metagame option list.
const CTA_METAGAME_KEYS = ['hammers', 'trail', 'puzzle'] as const;
type MetagameKey = (typeof CTA_METAGAME_KEYS)[number];

const isMetagameKey = (value: string): value is MetagameKey => (CTA_METAGAME_KEYS as readonly string[]).includes(value);

export type BannerCtaDeps = {
  startGame: (gameId: number) => void;
  inviteFriendsEnabled: boolean;
  openInviteFriends: () => void;
  tournamentOfChampionsAvailable: boolean;
  getTournamentCanOpenDialog: () => boolean;
  fetchTournament: () => Promise<void>;
  openTournamentDialog: () => void;
  crownSportsFeatureEnabled: boolean;
  sportEventAvailable: boolean;
  startSportEvent: () => void;
  crownJackpotAvailable: boolean;
  openCrownJackpot: () => void;
  benefitEventAvailable: (benefitKey: string) => boolean;
  openBenefitEvent: (benefitKey: string) => void;
  metagameAvailable: Record<MetagameKey, boolean>;
  startMetagame: Record<MetagameKey, () => void>;
};

const GAME_ID_PATTERN = /^[1-9][0-9]*$/;

// Hand-synced with admin-frontend's RivePreview click guard.
export const CTA_CLICK_COOLDOWN_MS = 1000;

export function guardRepeatClicks(
  action: () => void,
  cooldownMs = CTA_CLICK_COOLDOWN_MS,
  now: () => number = () => performance.now(),
) {
  let lastAcceptedAt = -Infinity;
  return () => {
    const timestamp = now();
    if (timestamp - lastAcceptedAt < cooldownMs) return;
    lastAcceptedAt = timestamp;
    action();
  };
}

export function resolveBannerCtaAction(cta: BannerCta | null, deps: BannerCtaDeps): (() => void) | null {
  if (!cta) return null;

  switch (cta.type) {
    case 'GAME': {
      if (!GAME_ID_PATTERN.test(cta.value)) return null;
      const gameId = Number(cta.value);
      if (!Number.isSafeInteger(gameId)) return null;
      return () => deps.startGame(gameId);
    }
    case 'POPUP':
      switch (cta.value) {
        case 'INVITE_FRIEND':
          return deps.inviteFriendsEnabled ? deps.openInviteFriends : null;
        case 'CHAMPIONS_TOURNAMENT':
          if (!deps.tournamentOfChampionsAvailable) return null;
          return () => {
            void (async () => {
              await deps.fetchTournament();
              if (!deps.getTournamentCanOpenDialog()) return;
              deps.openTournamentDialog();
            })();
          };
        case 'CROWN_SPORTS':
          return deps.crownSportsFeatureEnabled && deps.sportEventAvailable ? deps.startSportEvent : null;
        case 'CROWN_JACKPOT':
          return deps.crownJackpotAvailable ? deps.openCrownJackpot : null;
        default:
          return null;
      }
    case 'METAGAME':
      return isMetagameKey(cta.value) && deps.metagameAvailable[cta.value] ? deps.startMetagame[cta.value] : null;
    case 'BENEFIT': {
      if (!cta.value) return null;
      const key = cta.value;
      return deps.benefitEventAvailable(key) ? () => deps.openBenefitEvent(key) : null;
    }
    default:
      return null;
  }
}
